import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { consentApi } from "@/api/user";
import { CONSENT_VERSION, type CookieSettings } from "@/components/CookieBanner";

/**
 * Mirrors the user's localStorage-stored cookie banner choices into the
 * server-side `consent_records` table so that:
 *  - Settings → Privacy → Consent History can display a unified, complete
 *    audit trail (Art. 7(1) GDPR "demonstrate consent").
 *  - Cookie consent travels with the account across devices/browsers
 *    (the banner itself stays localStorage-driven because it has to work
 *    for anonymous visitors before login).
 *
 * Behavior:
 *  - On user login: read localStorage; for every cookies_* category whose
 *    server state is missing or differs, POST a back-fill record with
 *    method `cookie_banner_backfill`.
 *  - On `cookieConsentUpdated` event (dispatched by CookieBanner whenever
 *    the user saves a new choice): POST one record per category with
 *    method `cookie_banner`.
 *
 * The localStorage record remains the source of truth for the browser:
 *  - It must work before login.
 *  - The actual analytics/marketing scripts read from it via
 *    utils/analytics.ts.
 *  - This mirror is one-way (local → server), audit-only.
 */
const COOKIE_CONSENT_KEY = "cookieConsent";

const CATEGORY_TO_CONSENT_TYPE: Record<
  Exclude<keyof CookieSettings, "necessary">,
  string
> = {
  functional: "cookies_functional",
  analytics: "cookies_analytics",
  marketing: "cookies_marketing",
};

const readLocalSettings = (): CookieSettings | null => {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { settings?: CookieSettings };
    return parsed?.settings ?? null;
  } catch {
    return null;
  }
};

export const CookieConsentMirror = () => {
  const { user } = useAuth();

  // Back-fill on login. Only POST categories where the server's latest entry
  // is missing or disagrees with localStorage so we don't churn the audit
  // trail on every page load.
  useEffect(() => {
    if (!user) return;
    const local = readLocalSettings();
    if (!local) return;
    let cancelled = false;

    consentApi
      .getState(user.id)
      .then((server) => {
        if (cancelled) return;
        const state = server.state ?? {};
        for (const [category, consentType] of Object.entries(
          CATEGORY_TO_CONSENT_TYPE
        )) {
          const localGranted = !!local[category as keyof CookieSettings];
          const entry = state[consentType];
          const needs =
            !entry ||
            entry.granted !== localGranted ||
            entry.version !== CONSENT_VERSION;
          if (!needs) continue;
          consentApi
            .record(user.id, {
              consent_type: consentType,
              granted: localGranted,
              method: "cookie_banner_backfill",
              version: CONSENT_VERSION,
            })
            .catch((err) =>
              console.warn(
                "Cookie consent back-fill failed",
                consentType,
                err
              )
            );
        }
      })
      .catch((err) =>
        console.warn("Could not read server consent state for back-fill", err)
      );

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Live mirror of every banner/dialog save while logged in.
  useEffect(() => {
    if (!user) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CookieSettings>).detail;
      if (!detail) return;
      for (const [category, consentType] of Object.entries(
        CATEGORY_TO_CONSENT_TYPE
      )) {
        const granted = !!detail[category as keyof CookieSettings];
        consentApi
          .record(user.id, {
            consent_type: consentType,
            granted,
            method: "cookie_banner",
            version: CONSENT_VERSION,
          })
          .catch((err) =>
            console.warn(
              "Failed to mirror cookie consent change",
              consentType,
              err
            )
          );
      }
    };
    window.addEventListener("cookieConsentUpdated", handler);
    return () => window.removeEventListener("cookieConsentUpdated", handler);
  }, [user?.id]);

  return null;
};
